// Every call to the backend goes through here.
//
// There is no database client in the browser and no key of any kind: the API
// runs on the VPS behind Caddy at /api/*, holds the credentials, and verifies
// the admin session server-side before returning anything.
//
// credentials: 'same-origin' is required — the session is an httpOnly cookie,
// which fetch will not send otherwise.

export type AppointmentStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed';

export interface Appointment {
  id: number;
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  service: string;
  date: string;
  heure: string;
  message: string | null;
  status: AppointmentStatus;
  created_at: string;
  updated_at: string;
}

export interface AppointmentInput {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  service: string;
  date: string;
  heure: string;
  message?: string;
}

export class ApiError extends Error {
  status: number;
  errors?: string[];

  constructor(message: string, status: number, errors?: string[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      credentials: 'same-origin',
      headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
      ...init,
    });
  } catch {
    throw new ApiError('Connexion au serveur impossible', 0);
  }

  const body = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiError(
      body?.error || body?.errors?.[0] || 'Une erreur est survenue',
      res.status,
      body?.errors,
    );
  }

  return body as T;
}

// ── Public ──────────────────────────────────────────────────────

export function submitAppointment(input: AppointmentInput) {
  return request<{ ok: true }>('/appointments', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface Absence {
  id: number;
  from: string;
  to: string;
  label: string | null;
  created_at?: string;
}

/** Public: closed date ranges for the booking form. */
export function listPublicAbsences() {
  return request<Absence[]>('/absences');
}

// ── Admin ───────────────────────────────────────────────────────

export function adminLogin(password: string) {
  return request<{ ok: true }>('/admin/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export function adminLogout() {
  return request<{ ok: true }>('/admin/logout', { method: 'POST' });
}

// Resolves only if the session cookie is valid; 401 otherwise.
export function checkSession() {
  return request<{ ok: true }>('/admin/session');
}

export function listAppointments(status?: AppointmentStatus) {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  return request<Appointment[]>(`/admin/appointments${query}`);
}

export function updateAppointmentStatus(id: number, status: AppointmentStatus) {
  return request<{ appointment: Appointment; emailed: boolean; warning?: string }>(
    `/admin/appointments/${id}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
  );
}

export function listAdminAbsences() {
  return request<Absence[]>('/admin/absences');
}

export function createAbsence(input: { from: string; to: string; label?: string }) {
  return request<Absence>('/admin/absences', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function removeAbsence(id: number) {
  return request<{ ok: true }>(`/admin/absences/${id}`, { method: 'DELETE' });
}
